/**
 * Reading the CPU fleet, and turning it into plain English.
 *
 * Every function here is pure and says only what the server measured. The
 * honesty rule is sharper for cores than it is for GPUs: free cores are only
 * definable from a cluster-wide read, so when that read did not happen the
 * server publishes no count at all — and neither does this file. It returns
 * `null`, and the screen shows a sentence instead of a number.
 */

import type {
  CpuCapacity,
  CpuNode,
  CpuPoolInfo,
  CpuRun,
  CpuRunState,
  CpuRunTask,
  CpuTaskState,
  CpuWorkspace,
} from "../api/types";
import { TERMINAL_CPU_RUN_STATES } from "../api/types";
import {
  formatDuration,
  formatElapsed,
  formatEuroPerHour,
  formatRemaining,
  pluralize,
} from "./format";
import type { Tone } from "./workload";
import { isTerminalWorkspaceState } from "./workload";

/**
 * The sentences the CPU screen exists to say.
 *
 * They are here, beside the logic, because they are promises rather than
 * decoration: each one is a consequence of a decision in
 * `docs/adr/0012-cpu-runs-on-their-own-fleet.md` or
 * `docs/adr/0014-multi-machine-runs.md`, and a researcher who does not read
 * them meets the consequence anyway.
 */

/** What a big task costs, and why a small one is the way to use the fleet. */
export const WHOLE_MACHINE_NOTE =
  "A task that asks for a whole machine gets a whole machine, and bills for " +
  "the lot: 62 cores of a 64-core machine leaves nothing beside it. Ask for " +
  "what one copy of your work will really use and let the number of tasks do " +
  "the rest — small asks are what share machines, yours and other people's.";

/**
 * The one that surprises people, and the physics under it.
 *
 * A workspace is reclaimed when its GPUs stop computing. There is no
 * equivalent for cores and there will not be one: the collective and threading
 * libraries this work uses busy-wait, so a deadlocked parallel job and a
 * working one both read 100 %. A load-average rule would not be reading a
 * measurement the console lacks, it would be inventing one.
 */
export const NEVER_IDLE_NOTE =
  "A run is never stopped for being idle, and it never will be: nothing on a " +
  "CPU machine separates a computing process from a waiting one — the " +
  "libraries this work uses busy-wait, so a stuck parallel job and a busy one " +
  "both read 100 %. The console will not invent that measurement. The other " +
  "half of that promise is the half to remember: a run that is doing nothing " +
  "still costs money until its deadline. Set the deadline you mean.";

/** What bounds one task, what does not bound a run, and what neither is. */
export const MANY_MACHINES_NOTE =
  "One task is one command on the cores of one machine, and no task ever " +
  "spans machines: the most it can have is what the biggest CPU machine the " +
  "console can start really offers — a little under what the machine " +
  "advertises, because it keeps some of itself for itself, and less again " +
  "where memory runs out before cores do. A run is not bounded that way. Ask " +
  "for the command to run a thousand times and the console spreads those " +
  "tasks over as many machines as the fleet can give — a thousand one-core " +
  "tasks are about seventeen machines, not a thousand, because sixty-two of " +
  "them share a machine. Neither number is a ration: nobody here has cores " +
  "of their own.";

/**
 * Where a run's tasks work, said before anybody meets it.
 *
 * The two answers are different and the difference is the thing that surprises
 * everybody (`docs/adr/0018-run-scoped-work-volume.md`). A run of **one** task
 * works in `/home/<you>`, exactly as it always did. A run of **more than one**
 * cannot: a block disk attaches to one machine at a time and a fan-out is on
 * many, so those tasks mount no home folder at all and share `/work` instead —
 * which starts empty and is deleted with the run. `/scratch` is shared with
 * nobody: it is each **task's** own, even for two tasks that landed on the
 * same machine, which is the correction a fan-out is the first thing to need.
 * A researcher who assumes otherwise writes a thousand shards into what is
 * really a thousand folders, or into one that is deleted the moment the run
 * ends, and finds out at the end either way.
 */
export const SCRATCH_PER_MACHINE_NOTE =
  "A run of one task works in your home folder, the same files as your " +
  "workspace. A run of more than one does not see it at all: every machine of " +
  "the run shares one folder instead, /work — the same files at the same path " +
  "on all of them — and it starts empty, so your code and your libraries " +
  "travel in the environment you pick and your data arrives because a task " +
  "fetched it. /scratch is shared with nobody: each task has its own, even two " +
  "tasks that land on the same machine, and it goes when that task ends. " +
  "/work goes when the run ends, whichever way it ends, so anything worth " +
  "keeping has to be pushed somewhere that outlives the run — the team's " +
  "MLflow, a store of your own — before the task that made it exits.";

/**
 * Why several tasks can be refused, said where several tasks are asked for.
 *
 * The gate is whether this console has been given storage every machine of a
 * run can reach (`WORK_STORAGE_CLASS`), and this one sentence is read on both
 * kinds of console — so it states the rule rather than claiming which side of
 * it this deployment is on. The refusal itself is the server's (`503
 * not_connected`) and is rendered exactly as written; this only means nobody
 * meets it by surprise.
 */
export const MULTI_TASK_STORAGE_NOTE =
  "A run of more than one task works in one folder every machine of the run " +
  "can reach, and that folder is the run's own — your home folder is not on " +
  "those machines. Where a console has not been given shared run storage, " +
  "asking for more than one task is refused the moment you submit — in a " +
  "sentence that says so, and tells you to run it as one task meanwhile. " +
  "Everything else here is true either way.";

/** The one change a script needs, said where the task count is chosen. */
export const TASK_ID_NOTE =
  "The console runs the same command that many times and tells each copy " +
  "which one it is: $TASK_ID, 0 to tasks−1, and $SLURM_ARRAY_TASK_ID for " +
  "scripts that already read it. Tasks are independent — they do not talk to " +
  "each other, they start as machines free up rather than all at once, and " +
  "one that fails does not stop the others.";

/** One promise, as the line that fits on a screen and as the whole thing. */
export interface RunPromise {
  /** The sentence a researcher must not be able to miss. */
  headline: string;
  /** The promise in full, one click away rather than deleted. */
  full: string;
}

/**
 * What a run is, in four lines — with the four paragraphs behind them.
 *
 * The paragraphs are the promises and none of them may be lost, but a screen
 * that opens with four of them is a screen nobody reads to the end. So the
 * headline carries the consequence and the paragraph carries the reasoning,
 * and the reasoning is a disclosure away, never a page away.
 */
export const RUN_PROMISES: readonly RunPromise[] = [
  {
    headline:
      "One task is one machine. A run is as many machines as the fleet can " +
      "give it, and nobody here has cores of their own.",
    full: MANY_MACHINES_NOTE,
  },
  {
    headline:
      "A whole machine to one task is a whole machine paid for. Ask for what " +
      "one copy of your work really uses.",
    full: WHOLE_MACHINE_NOTE,
  },
  {
    headline:
      "One task works in your home folder. Many tasks work in /work, which " +
      "starts empty and is deleted with the run — push what you want to keep " +
      "before it ends.",
    full: SCRATCH_PER_MACHINE_NOTE,
  },
  {
    headline:
      "Nothing stops a run for being idle, so a run that is doing nothing " +
      "bills until its deadline. Set the deadline you mean.",
    full: NEVER_IDLE_NOTE,
  },
];

/**
 * The same physics as a run's, said to somebody who lives on the machine.
 *
 * A GPU workspace can be stopped for holding devices that are not computing.
 * There is no equivalent here and there will not be one, so the deadline is
 * not a safety net — it is the mechanism, and the second half of that promise
 * is the half people must be told.
 */
export const WORKSPACE_NEVER_IDLE_NOTE =
  "Nothing stops this workspace for being idle, and nothing ever will: " +
  "nothing on a CPU machine separates a computing process from a waiting one, " +
  "and the console will not invent that measurement. So it ends at its " +
  "deadline, or when you stop it, and at no other moment. The other half of " +
  "that promise is the half to remember: an idle CPU workspace bills to its " +
  "deadline. Set the deadline you mean.";

/** The one button that is missing, and the honest sentence instead of it. */
export const NO_RESIZE_NOTE =
  "To change the size, stop this workspace and start another one with more " +
  "cores. Your files in /home are untouched; /scratch and anything running " +
  "are not — which is exactly what changing the size of a GPU workspace costs " +
  "too. There is no resize button here, deliberately, and it may come in a " +
  "later version.";

/** What the request buys, and what it does not: the machine is shared. */
export const WORKSPACE_RESERVATION_NOTE =
  "The cores you asked for are yours and nobody can take them from you. You " +
  "share the machine with other people's tasks and workspaces whenever " +
  "everyone fits — what protects you is what you were given, not who else is " +
  "there.";

/** What `cores_free` means, said where the number is shown. */
export const RESERVABLE_NOT_IDLE_NOTE =
  "Free means free to be given out, not measured idle: these numbers come " +
  "from what the machines promised to each task, never from how hard anything " +
  "is working.";

const CPU_RUN_TONES: Record<CpuRunState, Tone> = {
  waiting_for_node: "pending",
  starting: "pending",
  running: "ok",
  expiring: "warning",
  succeeded: "done",
  failed: "danger",
  cancelled: "done",
  expired: "done",
};

/**
 * True when the run's deadline warning has arrived.
 *
 * Read from `warning_at` rather than from the state, because the deadline is
 * the only thing that bounds a running run: it deserves to be visible the
 * moment the server says the warning window opened, whichever word the state
 * carries at that instant.
 *
 * Args:
 *     run: The run as served by the API.
 *     now: Reference instant in milliseconds (injectable for tests).
 *
 * Returns:
 *     Whether the run is inside its warning window.
 */
export function isNearDeadline(run: CpuRun, now: number = Date.now()): boolean {
  if (run.warning_at === null || TERMINAL_CPU_RUN_STATES.includes(run.state)) {
    return false;
  }
  const warning = Date.parse(run.warning_at);
  return !Number.isNaN(warning) && warning <= now;
}

/**
 * Tone of a run, state first but not state alone.
 *
 * Args:
 *     run: The run, or just its state.
 *     now: Reference instant in milliseconds.
 *
 * Returns:
 *     The tone to render it in.
 */
export function cpuRunTone(
  run: CpuRun | CpuRunState,
  now: number = Date.now(),
): Tone {
  if (typeof run === "string") {
    return CPU_RUN_TONES[run];
  }
  return isNearDeadline(run, now) ? "warning" : CPU_RUN_TONES[run.state];
}

/**
 * Short state word for badges.
 *
 * The words are the ones jobs already use: a run is the batch primitive sized
 * in cores, and a researcher should not have to learn a second vocabulary for
 * the same seven states.
 *
 * Args:
 *     state: The run state.
 *
 * Returns:
 *     One or two words for the badge.
 */
export function cpuRunStateLabel(state: CpuRunState): string {
  switch (state) {
    case "waiting_for_node":
      return "Waiting";
    case "starting":
      return "Starting";
    case "running":
      return "Running";
    case "expiring":
      return "Expiring";
    case "succeeded":
      return "Finished";
    case "failed":
      return "Failed";
    case "cancelled":
      return "Stopped";
    case "expired":
      return "Out of time";
  }
}

/** What is true about a run that has no machine yet. */
function waitingHeadline(run: CpuRun, now: number): string {
  const queue =
    run.queue_position && run.queue_position > 1
      ? ` ${run.queue_position - 1} other ${
          run.queue_position === 2 ? "request is" : "requests are"
        } ahead of yours.`
      : "";
  if (run.machine_requested_at === null) {
    return (
      `Waiting for cores. No new machine is being started for this run, so it ` +
      `begins as soon as ${pluralize(run.cores, "core")} free up on a machine ` +
      `already running.${queue}`
    );
  }
  return (
    `No machine had ${pluralize(run.cores, "core")} free, so one was started ` +
    `for you ${formatElapsed(run.machine_requested_at, now)} ago. Your run ` +
    `begins by itself when it joins — you can close this page.${queue}`
  );
}

/**
 * How far a fan-out run has got, in the words its own trace uses.
 *
 * The sentence is the `progress` event's, deliberately: a researcher who read
 * *"847 of 1000 tasks finished, 0 failed"* in the trace should meet the same
 * count in the same words at the top of the page and in the list of runs, not
 * three dialects of one number.
 *
 * `null` for a run of one task, which is not a fan-out: *"1 of 1 tasks
 * finished"* is arithmetic rather than news, and everything a one-task run has
 * to say is already in its state and its exit code.
 *
 * Args:
 *     run: The run as served by the API.
 *
 * Returns:
 *     e.g. `847 of 1000 tasks finished, 3 failed`, or null.
 */
export function taskProgress(run: CpuRun): string | null {
  if (run.tasks <= 1) {
    return null;
  }
  return (
    `${run.tasks_succeeded} of ${run.tasks} tasks finished, ` +
    `${run.tasks_failed} failed`
  );
}

/**
 * The four counters, what the run holds, and the throttle it was given.
 *
 * Every number is one the server measured: the counters come from the task
 * rows, `machines` and `cores_running` from the cluster. None of them is
 * derived from the run's state, because no single state can stand for 150
 * waiting, 3 running and 847 finished at once.
 *
 * Args:
 *     run: The run as served by the API.
 *
 * Returns:
 *     Two or three sentences, or null for a run of one task.
 */
export function taskSummary(run: CpuRun): string | null {
  const progress = taskProgress(run);
  if (progress === null) {
    return null;
  }
  const parts = [
    `${progress} — ${run.tasks_running} running now, ` +
      `${run.tasks_waiting} still waiting for cores.`,
  ];
  if (run.machines > 0) {
    parts.push(
      `It holds ${pluralize(run.machines, "machine")} and ` +
        `${pluralize(run.cores_running, "core")} right now.`,
    );
  }
  const neverStarted = neverStartedNote(run);
  if (neverStarted !== null) {
    parts.push(neverStarted);
  }
  if (run.max_parallel_tasks !== null) {
    parts.push(
      `No more than ${run.max_parallel_tasks} of its tasks run at once, ` +
        `because you asked for that.`,
    );
  }
  return parts.join(" ");
}

/**
 * The tasks that never ran, said as what they are rather than as failures.
 *
 * `null` until there are some. "Failed" has to keep meaning *your code
 * failed*: a task the deadline arrived before is not a line of anybody's code
 * going wrong, and presenting it as one hands a researcher a list of failures
 * to debug that never ran.
 *
 * Args:
 *     run: The run as served by the API.
 *
 * Returns:
 *     One sentence, or null when every task got its turn.
 */
export function neverStartedNote(run: CpuRun): string | null {
  if (run.tasks_not_run <= 0) {
    return null;
  }
  return (
    `${run.tasks_not_run} never started — the run ended before they were ` +
    `given a machine, which is not the same thing as failing.`
  );
}

/** How a finished run reports what it actually did. */
function finishedHeadline(run: CpuRun): string {
  const progress = taskProgress(run);
  if (progress !== null) {
    return `${progress}.`;
  }
  return "Finished. The command ran to the end and exited 0.";
}

/** Why a run failed, pointing only at things that exist. */
function failedHeadline(run: CpuRun): string {
  const progress = taskProgress(run);
  if (progress !== null) {
    return `${progress}. What finished stays finished.`;
  }
  if (run.error) {
    const reason = run.error.trim().replace(/\.$/, "");
    return run.has_logs
      ? `Failed: ${reason}. The log below says more.`
      : `Failed: ${reason}.`;
  }
  if (run.exit_code !== null) {
    return `Failed — the command exited with code ${run.exit_code}. The log says why.`;
  }
  return "Failed. The log says why.";
}

/**
 * What a run that is working is doing, and what will stop it.
 *
 * A fan-out run says how far it has got in the same breath: the machines it
 * holds are news for about a minute, the count is news for the whole run.
 */
function runningHeadline(run: CpuRun, now: number): string {
  const progress = taskProgress(run);
  const head =
    progress === null
      ? `Running on ${pluralize(run.machines, "machine")}.`
      : `Running on ${pluralize(run.machines, "machine")} — ${progress}.`;
  if (run.expires_at === null) {
    return head;
  }
  const what = progress === null ? "the command finishes" : "the tasks finish";
  return (
    `${head} It stops in ${formatRemaining(run.expires_at, now)} unless ` +
    `${what} first, or you add time.`
  );
}

/**
 * The one sentence that explains a run.
 *
 * Args:
 *     run: The run as served by the API.
 *     now: Reference instant in milliseconds (injectable for tests).
 *
 * Returns:
 *     A sentence a researcher can act on, with no jargon.
 */
export function cpuRunHeadline(run: CpuRun, now: number = Date.now()): string {
  if (isNearDeadline(run, now)) {
    return (
      `This run stops in ${formatRemaining(run.expires_at, now)}, whether or ` +
      `not the command has finished. Add time if it still has work to do.`
    );
  }
  switch (run.state) {
    case "waiting_for_node":
      return waitingHeadline(run, now);
    case "starting":
      return run.error
        ? `Your run has not started yet: ${run.error.trim().replace(/\.$/, "")}. ` +
            `The console keeps trying; stop it and ask for fewer cores if it does not clear.`
        : "Starting — usually a minute or two while the environment is downloaded.";
    case "running":
      return runningHeadline(run, now);
    case "expiring":
      return `This run stops in ${formatRemaining(run.expires_at, now)}. Add time if it still has work to do.`;
    case "succeeded":
      return finishedHeadline(run);
    case "failed":
      return failedHeadline(run);
    // What survives depends on where the run was working: a run of one task
    // wrote into the home folder and that stays, a run of more than one had
    // /work and it is deleted with the run.
    case "cancelled":
      return run.tasks > 1
        ? "You stopped this run. /work went with it, so what a task pushed " +
            "somewhere else before it stopped is what is left."
        : "You stopped this run. What it had already written to your home folder is still there.";
    case "expired":
      return run.tasks > 1
        ? "Its deadline arrived, so the run was stopped. /work went with it " +
            "and each task's fast local folder went with its machine, so what " +
            "a task pushed somewhere else is what is left."
        : "Its deadline arrived, so the run was stopped. What it wrote to your " +
          "home folder is still there; the fast local folder went with the machine.";
  }
}

/**
 * What one machine would offer a command with none of the console's work on it.
 *
 * `cores_total − system_cores` is the machine's own share taken out and
 * nothing else: what is given to tasks right now is capacity, not somebody
 * else's property, because those tasks end. It is the *capacity* half of the
 * server's own bound, so a busy fleet still offers the size of a machine
 * instead of collapsing to zero — and it is never the pool's advertised
 * `cores_per_node`, which is about two cores above what a machine of that
 * shape really offers.
 *
 * Both counts are already whole cores rounded the way that cannot
 * over-promise, so this can land one core under what the server would accept.
 * That direction is the safe one: the form never offers a core the server
 * would refuse.
 *
 * It is the **cores** half only. What a machine can actually hold is also
 * bounded by the memory that feeds those cores, and the setting that says how
 * much a core is given is the server's — it is not published. On a lean shape
 * this figure can therefore be a little high, and the server's own refusal
 * says which half bound the offer and by how much; the form shows that
 * sentence exactly as it was written.
 *
 * Args:
 *     node: A CPU machine as measured.
 *
 * Returns:
 *     Cores an empty machine of this size would offer, or 0 when it was not
 *     measured — a machine still being created has nothing to measure yet.
 */
function machineTaskCores(node: CpuNode): number {
  if (node.cores_total === null || node.system_cores === null) {
    return 0;
  }
  return Math.max(0, node.cores_total - node.system_cores);
}

/**
 * The most cores one command may ask for, or `null` when nobody may be told.
 *
 * `null` has one meaning and it is not "zero": the console could not make the
 * read that defines a free core, so it has no number to offer and will not
 * guess one. The form refuses rather than submits against a figure the server
 * would have to reject anyway.
 *
 * Zero is never the answer either. `largest_free_machine_cores` is the free
 * block on the machines running *now*, and this fleet's steady state is no
 * machine at all — a `floor: 0` pool drains to zero, and every machine can be
 * full besides. Waiting for a machine to boot is not a refusal, so the bound
 * is what a started machine would offer: measured from the machines that exist
 * when there are any, and only otherwise the shape's advertised
 * `cores_per_node`, which is configuration and therefore an estimate. This is
 * the server's own bound (`services/cpu_runs.py::_core_ceiling`), so the two
 * halves refuse the same requests.
 *
 * Args:
 *     cpu: The CPU half of the capacity payload.
 *
 * Returns:
 *     The per-command ceiling, or null when nothing could be measured and no
 *     shape is configured to size a machine from.
 */
export function largestTaskCores(cpu: CpuCapacity): number | null {
  if (!cpu.measured) {
    return null;
  }
  const measured = Math.max(
    cpu.largest_free_machine_cores ?? 0,
    ...cpu.nodes.map(machineTaskCores),
  );
  if (measured > 0) {
    return measured;
  }
  const configured = Math.max(
    0,
    ...cpu.pools.map((pool) => pool.cores_per_node),
  );
  return configured > 0 ? configured : null;
}

/**
 * The sentence under the Cores field: what the bound is, and whose it is.
 *
 * The number alone would be read as "these cores are waiting for you", which
 * is true only when a running machine has that block free. When it does not,
 * the same number is the size of a machine that has to be started first, and
 * the hint says so rather than letting the researcher find out from the run's
 * own page.
 *
 * Args:
 *     cpu: The CPU half of the capacity payload.
 *
 * Returns:
 *     One sentence for the field's hint.
 */
export function coresHint(cpu: CpuCapacity): string {
  const ceiling = largestTaskCores(cpu);
  if (ceiling === null) {
    return (
      "Not measured right now, so the console cannot say how many cores one " +
      "task may have."
    );
  }
  if ((cpu.largest_free_machine_cores ?? 0) >= ceiling) {
    return (
      `Up to ${ceiling} — what the biggest machine the console can start ` +
      `really offers to one task.`
    );
  }
  return (
    `Up to ${ceiling} — the size of the biggest machine the console can ` +
    `start. No machine has that many free right now, so one is started for you.`
  );
}

/** How long a machine takes to start, or the admission that nobody knows. */
function bootWait(cpu: CpuCapacity): string {
  return cpu.node_start_eta_minutes !== null
    ? `usually about ${cpu.node_start_eta_minutes} min`
    : "how long that takes has never been timed here";
}

/**
 * Whether this many cores start now or wait for a machine to boot.
 *
 * The CPU counterpart of the GPU picker's per-choice sentence: the one thing
 * the form must say before anything is submitted is which of the two happens,
 * because the difference is seconds against minutes. Needing a machine is
 * never a refusal — the console starts one and the run begins by itself — so
 * the sentence says that too.
 *
 * "Free right now" is read from the machines' own `cores_free`, never from
 * `largest_free_machine_cores`: that field is the ceiling the form validates
 * against and it cannot be the free-now signal as well.
 *
 * The sentence is about **one task** — the first one — because that is what
 * the free cores decide. A run of a thousand tasks does not wait for a
 * thousand machines: it starts with as many as fit and the rest follow, which
 * is the second sentence rather than a different first one.
 *
 * Args:
 *     cores: The core count currently in the form.
 *     cpu: The CPU half of the capacity payload.
 *     tasks: How many times the command will run.
 *
 * Returns:
 *     One or two sentences, or null when there is nothing honest to say — the
 *     cores were not measured, or the field does not hold a whole positive
 *     number.
 */
export function coresStartNote(
  cores: number,
  cpu: CpuCapacity,
  tasks = 1,
): string | null {
  const first = firstTaskStartNote(cores, cpu);
  if (first === null || !Number.isInteger(tasks) || tasks <= 1) {
    return first;
  }
  return (
    `${first} Its ${tasks} tasks then start as machines free up, not all at ` +
    `once, and each one keeps its own log.`
  );
}

/** What the free cores say about the first task, and nothing else. */
function firstTaskStartNote(cores: number, cpu: CpuCapacity): string | null {
  if (!cpu.measured || !Number.isInteger(cores) || cores < 1) {
    return null;
  }
  const asked = pluralize(cores, "core");
  if (
    cpu.nodes.some((node) => node.cores_free !== null && node.cores_free >= cores)
  ) {
    return (
      `There ${cores === 1 ? "is" : "are"} ${asked} free on a machine that is ` +
      `already running — this starts in seconds.`
    );
  }
  if (cpu.nodes.some((node) => node.status === "provisioning")) {
    return (
      `No machine has ${asked} free, but one is already starting — ` +
      `${bootWait(cpu)}. Submit anyway: your run begins on its own when it ` +
      `is up.`
    );
  }
  if (
    cpu.machines_running !== null &&
    cpu.machines_max !== null &&
    cpu.machines_running >= cpu.machines_max
  ) {
    return (
      `No machine has ${asked} free, and the console is already running its ` +
      `${cpu.machines_max} CPU machines, so this run waits for cores to be ` +
      `given back.`
    );
  }
  return (
    `No machine has ${asked} free, so one is started for you — ` +
    `${bootWait(cpu)}. Submit anyway: your run begins on its own when the ` +
    `machine is up.`
  );
}

/**
 * One line for a run in the list of runs: its shape, and how far it has got.
 *
 * A run of one task keeps exactly the line it had before runs could fan out —
 * its cores and when it started — because nothing else is true about it. A
 * fan-out adds the two numbers the list is read for: how many copies, and how
 * many of them are done.
 *
 * Args:
 *     run: The run as served by the API.
 *     now: Reference instant in milliseconds (injectable for tests).
 *
 * Returns:
 *     e.g. `1000 tasks of 62 cores · 847 finished, 3 failed · started 2 h ago`.
 */
export function summarizeRunRow(run: CpuRun, now: number = Date.now()): string {
  const shape =
    run.tasks > 1
      ? `${run.tasks} tasks of ${pluralize(run.cores, "core")}`
      : pluralize(run.cores, "core");
  const started = run.started_at
    ? `started ${formatElapsed(run.started_at, now)} ago`
    : "started not yet";
  if (run.tasks <= 1) {
    return `${shape} · ${started}`;
  }
  return (
    `${shape} · ${run.tasks_succeeded} finished, ${run.tasks_failed} failed · ` +
    `${started}`
  );
}

/**
 * One sentence for the whole CPU fleet, or `null` when nothing was measured.
 *
 * The four numbers add up on purpose — usable = given out + free + the
 * machines' own share — so a researcher can check the arithmetic rather than
 * trust it.
 *
 * Args:
 *     cpu: The CPU half of the capacity payload.
 *
 * Returns:
 *     e.g. `126 usable cores on 2 machines — 62 given out, 62 free`, or null.
 */
export function summarizeCpuFleet(cpu: CpuCapacity): string | null {
  if (
    !cpu.measured ||
    cpu.cores_total === null ||
    cpu.cores_used === null ||
    cpu.cores_free === null
  ) {
    return null;
  }
  const machines = pluralize(cpu.nodes.length, "machine");
  return (
    `${cpu.cores_total} usable cores on ${machines} — ` +
    `${cpu.cores_used} given out, ${cpu.cores_free} free`
  );
}

/**
 * One sentence for a CPU pool.
 *
 * `max_nodes` is fleet-wide here — it is `MAX_CPU_MACHINES`, the ceiling
 * across every CPU pool — so the sentence says so rather than reading as a
 * per-pool number the way the GPU one does.
 *
 * Args:
 *     pool: The pool as published.
 *
 * Returns:
 *     e.g. `2 running, of the 64 CPU machines the console may run at once`.
 */
export function summarizeCpuPool(pool: CpuPoolInfo): string {
  const parts = [
    `${pool.ready_nodes} running, of the ${pool.max_nodes} CPU machines the ` +
      `console may run at once`,
  ];
  if (pool.provisioning_nodes > 0) {
    parts.push(`${pool.provisioning_nodes} starting`);
  }
  if (pool.unavailable_nodes > 0) {
    parts.push(`${pool.unavailable_nodes} stuck`);
  }
  // A machine on its way out is said plainly, and never as "stuck": the
  // console asked for it to go, it is going, and nobody has to do anything.
  if (pool.draining_nodes > 0) {
    parts.push(`${pool.draining_nodes} going back to Scaleway`);
  }
  if (pool.floor > 0) {
    parts.push(
      `${pool.floor} kept ready even when nobody is computing, and paid for ` +
        "the whole time",
    );
  }
  return parts.join(", ");
}

/**
 * What the CPU fleet costs an hour, or the reason there is no figure.
 *
 * Every euro in this console is a **rate for the fleet**: what the machines
 * cost while they run, and what they would cost at the ceiling. There is no
 * per-person total anywhere in the API and there must be none on a screen —
 * a personal running total is a ration with the refusal taken out
 * (`docs/adr/0008-node-shapes-on-demand.md`), and this product removed
 * rations.
 *
 * The rate is counted from the machines *asked for* rather than the machines
 * that joined, because a machine bills from the moment it is created; the
 * server does that arithmetic and this only says the sentence. When the server
 * could not price part of the fleet it publishes no number and a reason, and
 * the reason is what is shown — a figure that is wrong about part of the fleet
 * is worse than none.
 *
 * Args:
 *     cpu: The CPU half of the capacity payload.
 *
 * Returns:
 *     One or two sentences, or null when the server said nothing at all about
 *     money — a console that was given no rates says nothing rather than
 *     apologising for it on every screen.
 */
export function fleetSpendNote(cpu: CpuCapacity): string | null {
  if (cpu.fleet_per_hour_eur === null) {
    return cpu.fleet_per_hour_reason;
  }
  const parts = [
    `The CPU machines this console has asked for cost about ` +
      `${formatEuroPerHour(cpu.fleet_per_hour_eur)} an hour while they run, ` +
      `at the rates this console was given.`,
  ];
  if (cpu.ceiling_per_hour_eur !== null && cpu.machines_max !== null) {
    parts.push(
      `All ${cpu.machines_max} of them would be about ` +
        `${formatEuroPerHour(cpu.ceiling_per_hour_eur)} an hour — the most ` +
        `this fleet can cost, not a forecast that it will.`,
    );
  } else if (cpu.ceiling_per_hour_reason !== null) {
    parts.push(cpu.ceiling_per_hour_reason);
  }
  return parts.join(" ");
}

/**
 * Who holds a machine's cores, counted rather than named.
 *
 * Tasks and workspaces are two numbers because they end differently — a task
 * when its command exits, a workspace at its deadline — and somebody looking
 * at a machine that will not drain wants to know which of the two holds it.
 *
 * Args:
 *     node: A CPU machine as measured.
 *
 * Returns:
 *     e.g. `1 task and 2 workspaces`, or null when neither is on it — the
 *     cores are then somebody else's, and the sentence says only that.
 */
function holdersOfMachine(node: CpuNode): string | null {
  const parts: string[] = [];
  if (node.tasks !== null && node.tasks > 0) {
    parts.push(pluralize(node.tasks, "task"));
  }
  if (node.workspaces !== null && node.workspaces > 0) {
    parts.push(pluralize(node.workspaces, "workspace"));
  }
  return parts.length > 0 ? parts.join(" and ") : null;
}

/**
 * One sentence describing a CPU machine.
 *
 * Args:
 *     node: A CPU machine as measured.
 *
 * Returns:
 *     e.g. `63 usable cores — 62 given to 1 task, 0 free. 1 more is the
 *     machine's own.`
 */
export function summarizeCpuMachine(node: CpuNode): string {
  if (node.status === "provisioning") {
    return "This machine is still starting.";
  }
  if (node.status === "not_ready") {
    return "This machine is not accepting work.";
  }
  if (
    node.cores_total === null ||
    node.cores_used === null ||
    node.cores_free === null
  ) {
    return "The console could not measure this machine's cores, so it shows none.";
  }
  const holders = holdersOfMachine(node);
  const held = holders === null ? "given out" : `given to ${holders}`;
  const own =
    node.system_cores !== null && node.system_cores > 0
      ? ` ${pluralize(node.system_cores, "core")} more ${
          node.system_cores === 1 ? "is" : "are"
        } the machine's own.`
      : "";
  const memory =
    node.memory_free_gb !== null && node.memory_total_gb !== null
      ? ` ${node.memory_free_gb} of ${node.memory_total_gb} GiB of memory free.`
      : "";
  return (
    `${node.cores_total} usable cores — ${node.cores_used} ${held}, ` +
    `${node.cores_free} free.${own}${memory}`
  );
}

// ------------------------------------------------------------- the run form

export interface CpuRunFormValues {
  name: string;
  image: string;
  command: string;
  /** Cores **one task** needs — never the run's total. */
  cores: number;
  hours: number;
  /** How many times the command runs. 1 is a run of one command. */
  tasks: number;
  /** Extra attempts for a failed task, 0 unless the researcher asks. */
  retries: number;
  /** `null` — the field left empty — means as many at once as the fleet gives. */
  maxParallelTasks: number | null;
}

/** Field name to error message; empty when the form is valid. */
export type CpuRunFormErrors = Partial<Record<keyof CpuRunFormValues, string>>;

/**
 * Where the command starts, which is a different answer at one task and many.
 *
 * A run of one task starts in `/home/<you>`, the files a workspace sees. A run
 * of more than one starts in `/work` and its machines carry no home folder at
 * all (`docs/adr/0018-run-scoped-work-volume.md`). One sentence cannot be true
 * of both, so the hint follows the task count in the form rather than telling
 * one of the two researchers something false — and it changes under the
 * command as soon as the count does, which is before anything is submitted.
 *
 * Args:
 *     username: The submitter's own name, so the path is the one they know.
 *     tasks: How many times the command will run. Anything that is not a whole
 *         number above one is the one-task sentence: a half-typed field must
 *         never flip the promise.
 *
 * Returns:
 *     One sentence for the command field's hint.
 */
export function commandStartHint(username: string, tasks: number): string {
  if (!Number.isInteger(tasks) || tasks <= 1) {
    return `Runs in /home/${username} — the same files as your workspace.`;
  }
  return (
    `Runs in /work — one folder shared by every machine of the run, which ` +
    `starts empty and is deleted when the run ends. /home/${username} is not ` +
    `on those machines, so push anything worth keeping before a task exits.`
  );
}

/**
 * Words that say how a command is launched rather than what it runs.
 *
 * Naming every run after its interpreter would give a list of runs all called
 * `python-<date>`, which is the opposite of recognising one later. So the
 * suggestion walks past the launcher — and past the subcommand a launcher
 * takes, `uv run` and its kind — to the thing being launched.
 */
const COMMAND_LAUNCHERS: ReadonlySet<string> = new Set([
  "bash",
  "conda",
  "env",
  "exec",
  "julia",
  "make",
  "nohup",
  "node",
  "pipenv",
  "poetry",
  "python",
  "python2",
  "python3",
  "Rscript",
  "run",
  "sh",
  "time",
  "uv",
  "uvx",
  "xargs",
  "zsh",
]);

/** The last path segment, with any extension taken off. */
function commandStem(token: string): string {
  const base = token.split("/").pop() ?? token;
  return base.replace(/\.[^.]+$/, "");
}

/** Lower-case letters, digits and single hyphens; empty when nothing is left. */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/g, "");
}

/**
 * A name for a run its author has not named yet.
 *
 * An empty name is never what stops a run from starting: the field opens on
 * this, and it is a real name — what the command runs and the day it was
 * asked for — so a researcher who types a command and presses the button gets
 * a run they can still recognise in the list a week later. It is computed
 * here, never on the server, because it must be visible and editable before
 * anything is submitted.
 *
 * Args:
 *     command: The command as currently typed, possibly empty.
 *     now: Reference instant (injectable for tests).
 *
 * Returns:
 *     e.g. `featurise-2026-08-09`, or `run-2026-08-09` when the command names
 *     nothing usable yet.
 */
export function suggestedRunName(
  command: string,
  now: Date = new Date(),
): string {
  const pad = (value: number): string => String(value).padStart(2, "0");
  const day = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  for (const token of command.trim().split(/\s+/)) {
    // Flags say nothing about the work, and `VAR=value` is the environment
    // rather than the command.
    if (token === "" || token.startsWith("-") || token.includes("=")) {
      continue;
    }
    const stem = commandStem(token);
    if (COMMAND_LAUNCHERS.has(token) || COMMAND_LAUNCHERS.has(stem)) {
      continue;
    }
    const slug = slugify(stem);
    if (slug !== "") {
      return `${slug}-${day}`;
    }
  }
  return `run-${day}`;
}

/**
 * What the folded half of the run form is currently asking for.
 *
 * A closed disclosure must never hide a choice somebody made, so the summary
 * carries back anything that is no longer the default. `null` means everything
 * behind it is untouched: one task, no retry, no throttle.
 *
 * Args:
 *     values: The three fields the disclosure holds.
 *
 * Returns:
 *     e.g. `1000 tasks, 1 retry, at most 20 at once`, or null.
 */
export function fanOutSummary(
  values: Pick<CpuRunFormValues, "tasks" | "retries" | "maxParallelTasks">,
): string | null {
  const parts: string[] = [];
  if (Number.isFinite(values.tasks) && values.tasks > 1) {
    parts.push(pluralize(values.tasks, "task"));
  }
  if (Number.isFinite(values.retries) && values.retries > 0) {
    parts.push(pluralize(values.retries, "retry", "retries"));
  }
  if (values.maxParallelTasks !== null) {
    parts.push(`at most ${values.maxParallelTasks} at once`);
  }
  return parts.length > 0 ? parts.join(", ") : null;
}

/**
 * The bounds the form must obey — all of them the server's, none invented.
 *
 * `maxCores` is `capacity.cpu.largest_free_machine_cores` and `null` when the
 * console could not measure it; `maxHours` is `MAX_RUN_HOURS` and `maxTasks` is
 * `MAX_TASKS_PER_RUN`, both published in `capacity.cpu.defaults`.
 *
 * There is deliberately no `maxRetries`: `MAX_TASK_RETRIES` is not in the
 * capacity payload, so the form has no number to name and will not invent one.
 * Asking for more than the deployment serves is refused by the server, in the
 * server's own sentence, and shown exactly as it was written.
 */
export interface CpuRunLimits {
  maxCores: number | null;
  maxHours: number;
  maxTasks: number;
}

/**
 * Validate the run form.
 *
 * The server enforces every one of these again — this only gives a faster,
 * clearer message, in the same words. The core bound in particular is the
 * number the capacity payload published, never a configured figure and never a
 * count of anybody's usage.
 *
 * Args:
 *     values: The current form values.
 *     limits: The bounds published by the server.
 *
 * Returns:
 *     One message per invalid field.
 */
export function validateCpuRunForm(
  values: CpuRunFormValues,
  limits: CpuRunLimits,
): CpuRunFormErrors {
  const errors: CpuRunFormErrors = {};

  if (!values.name.trim()) {
    errors.name = "Give the run a name so you can recognise it later.";
  }
  if (!values.image) {
    errors.image = "Choose an environment.";
  }
  if (!values.command.trim()) {
    errors.command = "Write the command to run.";
  }

  if (!Number.isInteger(values.cores) || values.cores < 1) {
    errors.cores = "Ask for at least one core.";
  } else if (limits.maxCores === null) {
    errors.cores =
      "The console could not measure the CPU machines just now, so it cannot " +
      "say how many cores one command may have — and it will not guess. " +
      "Nothing can be submitted until that read works again.";
  } else if (values.cores > limits.maxCores) {
    errors.cores =
      `The biggest CPU machine the console can start offers ` +
      `${limits.maxCores} cores to one task, and you asked for ` +
      `${values.cores}. Ask for ${limits.maxCores} or fewer — that is the ` +
      `size of a machine, not a ration. A run reaches past one machine by ` +
      `running more tasks, never by making one task bigger.`;
  }

  if (!Number.isFinite(values.hours) || values.hours < 1) {
    errors.hours = "A run needs at least one hour.";
  } else if (values.hours > limits.maxHours) {
    errors.hours =
      `A run may last up to ${limits.maxHours} h. Ask for ${limits.maxHours} ` +
      `or fewer — you can add time later, in one click.`;
  }

  if (!Number.isInteger(values.tasks) || values.tasks < 1) {
    errors.tasks = "A run runs its command at least once.";
  } else if (values.tasks > limits.maxTasks) {
    errors.tasks =
      `A run carries at most ${pluralize(limits.maxTasks, "task")}, and you ` +
      `asked for ${values.tasks}. Split it into several runs — that number ` +
      `guards one request, it is not a share of the fleet.`;
  }

  if (!Number.isInteger(values.retries) || values.retries < 0) {
    errors.retries =
      "Retries are a whole number of extra attempts, or none at all.";
  }

  if (
    values.maxParallelTasks !== null &&
    (!Number.isInteger(values.maxParallelTasks) || values.maxParallelTasks < 1)
  ) {
    errors.maxParallelTasks =
      "Leave this empty to let as many run at once as the fleet will give, " +
      "or hold the run to at least one at a time.";
  }

  return errors;
}

// ------------------------------------------------------- the workspace offer

/** One "how many cores" button on the CPU workspace form. */
export interface CoreChoice {
  count: number;
  /** The workspace would open immediately, with no machine to start. */
  immediate: boolean;
  /** One sentence explaining what happens if you pick it, always present. */
  reason: string;
}

/**
 * The core counts a CPU workspace may be asked for, each with its consequence.
 *
 * The counts are the server's filtered list and nothing else: it is the very
 * list `400 invalid_core_count` validates against, so a button here can never
 * be a size the console would then refuse. The **sentence** is the second
 * half, and it answers the only question the researcher has before clicking —
 * does this open in seconds, or does a machine have to start first? Asking for
 * more than is free is not a refusal, so no choice is ever taken away for it.
 *
 * Args:
 *     cpu: The CPU half of the capacity payload.
 *
 * Returns:
 *     One entry per offered count, in the order the server published them.
 */
export function workspaceCoreChoices(cpu: CpuCapacity): CoreChoice[] {
  return cpu.defaults.allowed_workspace_cores.map((count) => ({
    count,
    immediate: startsNow(count, cpu),
    reason: workspaceCoreReason(count, cpu),
  }));
}

/**
 * Whether this many cores are free on one machine right now.
 *
 * `largest_free_machine_cores`, and never the fleet's `cores_free`: a CPU
 * workspace is one pod on one machine, so cores spread over two machines are
 * not a machine this could open on.
 */
function startsNow(count: number, cpu: CpuCapacity): boolean {
  return (cpu.largest_free_machine_cores ?? 0) >= count;
}

/** What happens if this count is picked, in one sentence. */
function workspaceCoreReason(count: number, cpu: CpuCapacity): string {
  if (startsNow(count, cpu)) {
    return "Free right now — opens in seconds.";
  }
  if (cpu.nodes.some((node) => node.status === "provisioning")) {
    return (
      `No machine has ${count} free, but one is already starting — ` +
      `${bootWait(cpu)}. Ask anyway: your workspace opens on its own when it ` +
      `is up.`
    );
  }
  if (
    cpu.machines_running !== null &&
    cpu.machines_max !== null &&
    cpu.machines_running >= cpu.machines_max
  ) {
    return (
      `No machine has ${count} free, and the console is already running its ` +
      `${cpu.machines_max} CPU machines. Cores free up as work finishes.`
    );
  }
  return (
    `No machine has ${count} free, so one is started for you — ` +
    `${bootWait(cpu)}. Ask anyway: your workspace opens on its own when the ` +
    `machine is up.`
  );
}

/**
 * Refuse a core count the server does not offer, in the server's own words.
 *
 * The form only shows the published counts, so this catches the one way
 * another number can be reached: the offer shrinking under a chosen button —
 * the fleet re-keyed to a smaller shape, a machine measured lower than the
 * pool advertises. The message is the one the server would send, and it names
 * the server's number rather than a bound this bundle invented.
 *
 * Args:
 *     cores: The count about to be submitted.
 *     cpu: The CPU half of the capacity payload.
 *
 * Returns:
 *     The refusal, or null when the count is one the console offers.
 */
export function workspaceCoreError(
  cores: number,
  cpu: CpuCapacity,
): string | null {
  const offered = cpu.defaults.allowed_workspace_cores;
  if (offered.includes(cores)) {
    return null;
  }
  if (offered.length === 0) {
    return (
      "This console offers no CPU workspace size at all right now — no " +
      "machine it can start could hold one. Ask the platform team."
    );
  }
  const biggest = Math.max(...offered);
  if (cores > biggest) {
    return (
      `The biggest CPU machine the console can start offers ` +
      `${pluralize(biggest, "core")} to one workspace, and you asked for ` +
      `${cores}. Ask for ${biggest} or fewer — that is the size of a machine, ` +
      `not a ration.`
    );
  }
  return (
    `You can ask for ${offered.join(", ")} cores — not ${cores}. A workspace ` +
    `always runs on a single machine, and those are the sizes this console ` +
    `offers on one.`
  );
}

/**
 * What is true about a CPU workspace that has no machine yet.
 *
 * The server's sentence when there is one — it knows the CPU lane, the machine
 * being started for the request ahead and the refusal that stopped one being
 * started, and this file knows none of them. What follows is the fallback for
 * a bundle newer than the API it is talking to, in the wording it had before.
 */
function workspaceWaitingHeadline(
  workspace: CpuWorkspace,
  cores: string,
  now: number,
): string {
  if (workspace.waiting) {
    return workspace.waiting.message;
  }
  const queue =
    workspace.queue_position && workspace.queue_position > 1
      ? ` ${workspace.queue_position - 1} other ${
          workspace.queue_position === 2 ? "request is" : "requests are"
        } ahead of yours.`
      : "";
  if (workspace.machine_requested_at === null) {
    return (
      `Waiting for cores. No new machine is being started for this request, ` +
      `so your ${cores} open as soon as that many are free on one ` +
      `machine.${queue}`
    );
  }
  return (
    `No machine had ${cores} free, so one was started for you ` +
    `${formatElapsed(workspace.machine_requested_at, now)} ago. Your ` +
    `workspace opens by itself when it joins — you can close this ` +
    `page.${queue}`
  );
}

/**
 * Why a CPU workspace failed, pointing only at things that exist.
 *
 * The split is on `started_at` and not on the state, for the same reason as a
 * GPU workspace's: `failed` is reached both by a workspace whose environment
 * never arrived and by one that ran for hours and was then stopped from
 * underneath, and telling the second person it "could not start" sends them
 * looking through a startup log for a program that had been running.
 */
function workspaceFailedHeadline(workspace: CpuWorkspace): string {
  if (workspace.started_at === null) {
    if (workspace.error) {
      const reason = workspace.error.trim().replace(/\.$/, "");
      return workspace.has_logs
        ? `This workspace could not start: ${reason}. The startup log says more.`
        : `This workspace could not start: ${reason}. Nothing was lost — ask again.`;
    }
    if (workspace.has_logs) {
      return "This workspace could not start. The startup log says why.";
    }
    // The same rule as a GPU workspace's, for the same reason: a container
    // that started, crashed and was ended before it was ever usable has no
    // `started_at`, and it did reach a machine.
    return workspace.node === null
      ? "This workspace could not start, and it never reached a machine, so there is no log to read. Nothing was lost — ask again."
      : "This workspace could not start. The machine it was on has been given back, so there is no log left to read. Nothing was lost — ask again.";
  }

  const reason = workspace.error?.trim().replace(/\.$/, "") ?? null;
  if (reason) {
    return workspace.has_logs
      ? `This workspace stopped: ${reason}. The log is the last thing it wrote, and your files in /home are still there.`
      : `This workspace stopped: ${reason}. Your files in /home are still there.`;
  }
  return workspace.has_logs
    ? "This workspace stopped. The log is the last thing it wrote, and your files in /home are still there."
    : "This workspace stopped. Your files in /home are still there.";
}

/**
 * The one sentence that explains a CPU workspace.
 *
 * The workspace sentences with cores where the GPU count was, and **no idle
 * branch at all**: this kind has no idle countdown, so there is no state in
 * which the console could say a machine is doing nothing.
 *
 * Args:
 *     workspace: The CPU workspace as served by the API.
 *     now: Reference instant in milliseconds (injectable for tests).
 *
 * Returns:
 *     A sentence a researcher can act on, with no jargon.
 */
export function cpuWorkspaceHeadline(
  workspace: CpuWorkspace,
  now: number = Date.now(),
): string {
  const cores = pluralize(workspace.cores, "core");
  const verb = workspace.cores === 1 ? "is" : "are";
  switch (workspace.state) {
    case "waiting_for_node":
      return workspaceWaitingHeadline(workspace, cores, now);
    case "starting":
      if (workspace.error) {
        const reason = workspace.error.trim().replace(/\.$/, "");
        return (
          `Your workspace has not started yet: ${reason}. The console keeps ` +
          `trying; stop it and ask for fewer cores if it does not clear.`
        );
      }
      return workspace.node
        ? `Your workspace is starting on ${workspace.node} — usually a minute or two while the environment is downloaded.`
        : "Your request is with the cluster, waiting for a machine to take it — usually seconds.";
    case "ready":
      return workspace.expires_at
        ? `Ready. ${cores} ${verb} yours for another ${formatRemaining(workspace.expires_at, now)}.`
        : `Ready. ${cores} ${verb} yours.`;
    case "expiring":
      return (
        `This workspace stops in ` +
        `${formatRemaining(workspace.expires_at, now)}. Add time if you still ` +
        `need it — nothing else will stop it, and nothing will keep it either.`
      );
    case "stopped":
      return "Stopped. Everything you saved in your home folder is still there.";
    case "expired":
      return (
        "Its deadline arrived, so the workspace was stopped. Everything you " +
        "saved in your home folder is still there; /scratch went with the " +
        "machine."
      );
    case "failed":
      return workspaceFailedHeadline(workspace);
  }
}

// ------------------------------------------------------- the tasks of a run

const CPU_TASK_TONES: Record<CpuTaskState, Tone> = {
  waiting: "pending",
  running: "ok",
  succeeded: "done",
  failed: "danger",
  // Never `danger`. A task that never received a container is not a failure,
  // and the contract forbids a screen presenting it as one.
  not_run: "done",
};

/**
 * Tone of one task, in the five the console already has.
 *
 * Args:
 *     state: The task's state.
 *
 * Returns:
 *     The tone to render it in.
 */
export function taskTone(state: CpuTaskState): Tone {
  return CPU_TASK_TONES[state];
}

/**
 * Short state word for a task's badge.
 *
 * The words a run already uses for itself, plus the one only a task has:
 * *never started*, said in as many words rather than left to be read as a
 * failure.
 *
 * Args:
 *     state: The task's state.
 *
 * Returns:
 *     One or two words for the badge.
 */
export function taskStateLabel(state: CpuTaskState): string {
  switch (state) {
    case "waiting":
      return "Waiting";
    case "running":
      return "Running";
    case "succeeded":
      return "Finished";
    case "failed":
      return "Failed";
    case "not_run":
      return "Never started";
  }
}

/** How long a task ran, or how long it has been running. */
function taskDuration(task: CpuRunTask, now: number): string | null {
  if (task.started_at === null) {
    return null;
  }
  const started = Date.parse(task.started_at);
  if (Number.isNaN(started)) {
    return null;
  }
  if (task.finished_at === null) {
    return `running for ${formatElapsed(task.started_at, now)}`;
  }
  const finished = Date.parse(task.finished_at);
  return Number.isNaN(finished)
    ? null
    : `ran ${formatDuration(finished - started)}`;
}

/**
 * One line for a task row: where it ran, for how long, and how it ended.
 *
 * Only what the row itself carries. `machine` is the console's own name for
 * the machine and is `null` until the task has one; `exit_code` belongs to the
 * attempt this row describes, and a task stopped at the run's deadline never
 * got one.
 *
 * Args:
 *     task: One task as served by the API.
 *     now: Reference instant in milliseconds (injectable for tests).
 *
 * Returns:
 *     e.g. `terminus · ran 2 min · exit 3`, or a sentence when it has not run.
 */
export function summarizeTaskRow(
  task: CpuRunTask,
  now: number = Date.now(),
): string {
  const parts: string[] = [];
  if (task.machine !== null) {
    parts.push(task.machine);
  }
  const duration = taskDuration(task, now);
  if (duration !== null) {
    parts.push(duration);
  }
  if (task.exit_code !== null) {
    parts.push(`exit ${task.exit_code}`);
  }
  if (task.attempt > 0) {
    // `attempt` counts from 0 and says which try this row describes, so a row
    // at 1 is a task that was run again once. Said that way round, because
    // "attempt 1" reads as the first one.
    parts.push(`tried again ${pluralize(task.attempt, "time")}`);
  }
  if (parts.length === 0) {
    return "No machine yet.";
  }
  return parts.join(" · ");
}

/**
 * What asking for this task's log would find, in words rather than a slug.
 *
 * The three answers mean different things and none of them may be used for
 * another: nothing written yet, a copy the console kept, or a machine that has
 * been given back with nothing kept.
 *
 * Args:
 *     log: The row's own `log` field.
 *
 * Returns:
 *     A short label for the row.
 */
export function taskLogLabel(log: CpuRunTask["log"]): string {
  switch (log) {
    case "kept":
      return "Log";
    case "gone":
      return "no log kept";
    case "pending":
      return "nothing written yet";
  }
}

/** One segment of the stacked bar: a counter, and what it is called. */
export interface TaskSegment {
  /** The `data-segment` hook the stylesheet keys on. */
  segment: "done" | "failed" | "not_run" | "running" | "waiting";
  count: number;
  /** Share of the run's tasks, 0–100. */
  percent: number;
}

/**
 * The five counters as the shares of one bar.
 *
 * `not_run` is a segment of its own rather than part of `failed`, for the
 * reason the counter exists at all. Empty segments are dropped rather than
 * drawn at zero width, so a run with nothing failed has no failure colour on
 * its bar at all.
 *
 * Args:
 *     run: The run as served by the API.
 *
 * Returns:
 *     The segments with something in them, finished first, or an empty list
 *     when the run has no tasks to divide.
 */
export function taskSegments(run: CpuRun): TaskSegment[] {
  if (run.tasks <= 0) {
    return [];
  }
  const counts: [TaskSegment["segment"], number][] = [
    ["done", run.tasks_succeeded],
    ["failed", run.tasks_failed],
    ["not_run", run.tasks_not_run],
    ["running", run.tasks_running],
    ["waiting", run.tasks_waiting],
  ];
  return counts
    .filter(([, count]) => count > 0)
    .map(([segment, count]) => ({
      segment,
      count,
      percent: (count / run.tasks) * 100,
    }));
}

// --------------------------------------------------- the same run, as a line

/** Characters `shlex.quote` leaves alone, which are also safe in a shell. */
const SHELL_SAFE = /^[A-Za-z0-9@%+=:,./_-]+$/;

/**
 * Quote one word so a shell hands it over unchanged.
 *
 * The rule `shlex.quote` uses, because `nk` re-joins the words it is given
 * with `shlex.join`: a word of safe characters is passed as it is, anything
 * else goes inside single quotes with its own quotes broken out.
 *
 * Args:
 *     word: The word to quote.
 *
 * Returns:
 *     The word, safe to paste into a shell.
 */
export function shellQuote(word: string): string {
  if (word !== "" && SHELL_SAFE.test(word)) {
    return word;
  }
  return `'${word.split("'").join("'\\''")}'`;
}

/**
 * The command as words a shell will hand to `nk` unchanged.
 *
 * A command of plain words survives the round trip exactly: the shell splits
 * it, `nk` joins it back, and the console stores the same string. A command
 * carrying anything a shell would act on — `$TASK_ID`, a pipe, a quote — does
 * not, and cannot: there is no word a shell can pass that `shlex.join` turns
 * back into `$TASK_ID`. So it goes inside `bash -c '…'`, which is the idiom
 * `nk run --help` and `docs/CONVENTIONS.md` already give for exactly this, and
 * it runs the same command for the same reason.
 */
function commandWords(command: string): string {
  const words = command.trim().split(/\s+/);
  if (words.every((word) => SHELL_SAFE.test(word))) {
    return words.join(" ");
  }
  return `bash -c ${shellQuote(command.trim())}`;
}

/**
 * The `nk run` line that submits this run again.
 *
 * Built from the run's own published fields and from nothing else — no
 * default this page happens to hold, no number the server did not send. A flag
 * is written only where the run really carries that value, so a run of one
 * task has no `--tasks` and a run nobody throttled has no `--max-parallel-tasks`.
 *
 * `null` for a run whose `command` is not published: it is stripped for
 * anybody but the owner and an admin, and a line with an empty command would
 * be an invitation to submit nothing.
 *
 * Args:
 *     run: The run as served by the API.
 *
 * Returns:
 *     e.g. `nk run --cores 4 --tasks 1000 --hours 6 --image cpu-base --name
 *     featurise-shards -- python featurise.py`, or null.
 */
export function reproduceAsNkCommand(run: CpuRun): string | null {
  if (run.command.trim() === "") {
    return null;
  }
  const parts = ["nk run", `--cores ${run.cores}`];
  if (run.tasks > 1) {
    parts.push(`--tasks ${run.tasks}`);
  }
  parts.push(`--hours ${run.hours}`, `--image ${shellQuote(run.image)}`);
  parts.push(`--name ${shellQuote(run.name)}`);
  if (run.retries > 0) {
    parts.push(`--retries ${run.retries}`);
  }
  if (run.max_parallel_tasks !== null) {
    parts.push(`--max-parallel-tasks ${run.max_parallel_tasks}`);
  }
  parts.push(`-- ${commandWords(run.command)}`);
  return parts.join(" ");
}

/** What the line above is for, and the one thing it cannot do for you. */
export const REPRODUCE_NOTE =
  "The same run, as one line for a terminal. There is no per-task retry and " +
  "no partial resubmission: to redo only what failed, read the indices above " +
  "and submit a run whose command maps $TASK_ID onto them.";

// ----------------------------------------------------- the launcher terminal

/**
 * What the third card offers, in the size the server published.
 *
 * The size is a derivation and never a rule: the smallest count this console
 * offers, whatever that is today. The two things worth knowing before asking
 * for one are both here — a terminal is a reservation on a machine rather than
 * a machine, so a team's terminals cost one machine between them; and while it
 * is the only thing running, it keeps that machine awake and billing until its
 * deadline.
 *
 * Args:
 *     cores: The smallest core count `allowed_workspace_cores` offers.
 *
 * Returns:
 *     The paragraph under the card.
 */
export function launcherNote(cores: number): string {
  return (
    `A CPU workspace of ${pluralize(cores, "core")} — the smallest this ` +
    `console offers — with the nk command line already in it. Sign it in once ` +
    `with a key from your account screen and you submit runs, follow them, ` +
    `read a task's log, add time and stop them from a shell that is already ` +
    `inside the network. It is a CPU workspace and nothing new: your own ` +
    `/home, the same SSH line, the same deadline, the same stop button. Its ` +
    `local disk is a few gigabytes — a shell, a checkout and some log files, ` +
    `not a place to put data. Terminals share machines, so the team's cost ` +
    `one machine between them and not one each; but while yours is the only ` +
    `thing running, it keeps a whole machine awake and that machine bills ` +
    `until you stop the terminal or its deadline arrives. Set the deadline ` +
    `you mean, here more than anywhere.`
  );
}

/**
 * One line for a CPU workspace in a list: its size, and how long it has left.
 *
 * Args:
 *     workspace: The workspace as served by the API.
 *     now: Reference instant in milliseconds (injectable for tests).
 *
 * A workspace that has ended keeps its `expires_at`, so the state has to gate
 * the countdown: the field is history by then, and a row that reads it says
 * "stops in 6 h 12" about a workspace that stopped this morning.
 *
 * Returns:
 *     e.g. `32 cores · stops in 6 h 12`.
 */
export function summarizeCpuWorkspaceRow(
  workspace: CpuWorkspace,
  now: number = Date.now(),
): string {
  const size = pluralize(workspace.cores, "core");
  if (isTerminalWorkspaceState(workspace.state)) {
    return `${size} · not running any more`;
  }
  if (workspace.expires_at === null) {
    return `${size} · the clock starts when it opens`;
  }
  return `${size} · stops in ${formatRemaining(workspace.expires_at, now)}`;
}
